import { useState } from "react";
import { Card, CardHeader, CardBody } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { Icon } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { useConfirm } from "../../components/ConfirmDialog";
import { SyncSettingsPanel } from "../sync/SyncSettingsPanel";
import { UpdatesPanel } from "../updates/UpdatesPanel";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import { isWebClient } from "../../ipc/http-transport";
import { AlertsPanel } from "../alerts/AlertsPanel";
import { ServerStatusPanel } from "../alerts/ServerStatusPanel";
import type { CredentialsStatus, CredentialProvider, CredentialTestResult, BackupList, BusinessConfig } from "../../ipc/types";
import "../dashboard/dashboard.css";
import "../product-development/product-development.css";

interface FieldDef { readonly key: string; readonly label: string; readonly secret: boolean; readonly placeholder: string }

const ML_FIELDS: readonly FieldDef[] = [
  { key: "clientId", label: "Client ID (App ID)", secret: false, placeholder: "Ej: 1234567890123456" },
  { key: "clientSecret", label: "Client Secret", secret: true, placeholder: "Secret de tu aplicación ML" },
  { key: "refreshToken", label: "Refresh Token", secret: true, placeholder: "Token de renovación OAuth" },
];

const DEFAULT_REDIRECT_URI = "https://localhost/callback";

/** Conexión OAuth real con Mercado Libre en dos pasos: (1) abrir la autorización en el navegador,
 *  (2) pegar el código de la redirección para canjear el primer refresh token. Claude nunca maneja
 *  la contraseña del usuario: el inicio de sesión ocurre en el navegador del propio usuario. */
export function MlConnectPanel({ configured, masked, onSaved }: { configured: boolean; masked: string; onSaved: () => void }): JSX.Element {
  const client = useIpc();
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [redirectUri, setRedirectUri] = useState(DEFAULT_REDIRECT_URI);
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState<"begin" | "complete" | "test" | null>(null);
  const [result, setResult] = useState<CredentialTestResult | null>(null);

  const begin = async (): Promise<void> => {
    setBusy("begin"); setResult(null);
    const res = await client.mlBeginConnect({ clientId, clientSecret, redirectUri });
    setBusy(null);
    if (!res.ok) { setResult({ ok: false, detail: res.error.message }); return; }
    setAuthUrl(res.value.authUrl);
    // El main de Electron intercepta window.open y lo abre en el navegador externo (setWindowOpenHandler).
    window.open(res.value.authUrl, "_blank", "noopener,noreferrer");
  };
  const complete = async (): Promise<void> => {
    setBusy("complete"); setResult(null);
    const res = await client.mlCompleteConnect(code);
    setBusy(null);
    const r = res.ok ? res.value : { ok: false, detail: res.error.message };
    setResult(r);
    if (r.ok) { setCode(""); setAuthUrl(null); onSaved(); }
  };
  const test = async (): Promise<void> => {
    setBusy("test"); setResult(null);
    const res = await client.testCredentials("mercadolibre");
    setBusy(null);
    setResult(res.ok ? res.value : { ok: false, detail: res.error.message });
  };

  return (
    <Card>
      <CardHeader
        title={<span className="row" style={{ gap: 10 }}><Icon name="marketplace" size={18} /> Mercado Libre</span>}
        subtitle="Conecta tu cuenta con OAuth real para publicar, sincronizar precio/stock e importar órdenes"
        action={configured ? <Badge tone="success" dot>Conectado</Badge> : <Badge tone="warning" dot>Sin conectar</Badge>}
      />
      <CardBody>
        {configured ? (
          <p className="muted" style={{ marginTop: 0, fontSize: 13.5 }}>Cuenta conectada · acceso guardado: <b className="strong">{masked || "•••"}</b>. Puedes reconectar cuando quieras repitiendo los pasos.</p>
        ) : (
          <p className="muted" style={{ marginTop: 0, fontSize: 13.5 }}>Necesitas una <b className="strong">aplicación</b> creada en <span className="strong">developers.mercadolibre.cl</span> (te da App ID, Secret y una URL de redirección).</p>
        )}

        {/* Paso 1 */}
        <div className="callout" style={{ marginBottom: "var(--sp-4)" }}>
          <div className="bar" />
          <div className="body"><b>Paso 1.</b> Ingresa los datos de tu aplicación y abre la autorización. Iniciarás sesión en Mercado Libre en tu navegador (tu contraseña nunca pasa por aquí).</div>
        </div>
        <div className="pd-form">
          <div className="pd-field pd-form__full">
            <span className="pd-field__label">App ID (Client ID)</span>
            <input className="pd-input" type="text" autoComplete="off" placeholder="Ej: 1234567890123456" value={clientId} aria-label="App ID" onChange={(e) => setClientId(e.target.value)} />
          </div>
          <div className="pd-field pd-form__full">
            <span className="pd-field__label">Secret (Client Secret)</span>
            <input className="pd-input" type="password" autoComplete="off" placeholder="Secret de tu aplicación" value={clientSecret} aria-label="Secret" onChange={(e) => setClientSecret(e.target.value)} />
          </div>
          <div className="pd-field pd-form__full">
            <span className="pd-field__label">URL de redirección (redirect_uri)</span>
            <input className="pd-input" type="text" autoComplete="off" placeholder={DEFAULT_REDIRECT_URI} value={redirectUri} aria-label="URL de redirección" onChange={(e) => setRedirectUri(e.target.value)} />
            <span className="faint" style={{ fontSize: 12.5 }}>Debe coincidir EXACTAMENTE con la configurada en tu aplicación de Mercado Libre.</span>
          </div>
        </div>
        <div className="pd-actions">
          <Button size="sm" icon="marketplace" disabled={busy !== null} onClick={begin}>
            {busy === "begin" ? "Abriendo…" : "Conectar con Mercado Libre"}
          </Button>
          {configured ? (
            <Button variant="secondary" size="sm" icon="pulse" disabled={busy !== null} onClick={test}>
              {busy === "test" ? "Probando…" : "Probar conexión"}
            </Button>
          ) : null}
        </div>

        {/* Paso 2 */}
        {authUrl ? (
          <>
            <div className="callout" style={{ margin: "var(--sp-4) 0" }}>
              <div className="bar" />
              <div className="body"><b>Paso 2.</b> Autoriza en la pestaña que se abrió. Mercado Libre te redirigirá a tu URL con un <code>?code=…</code> al final. Copia esa URL completa (o solo el código) y pégala aquí.
                <br /><span className="faint" style={{ fontSize: 12.5 }}>¿No se abrió? <a href={authUrl} target="_blank" rel="noopener noreferrer">Abrir autorización manualmente</a>.</span>
              </div>
            </div>
            <div className="pd-form">
              <div className="pd-field pd-form__full">
                <span className="pd-field__label">Código o URL de redirección</span>
                <input className="pd-input" type="text" autoComplete="off" placeholder="https://localhost/callback?code=TG-… (o solo TG-…)" value={code} aria-label="Código de autorización" onChange={(e) => setCode(e.target.value)} />
              </div>
            </div>
            <div className="pd-actions">
              <Button size="sm" icon="check" disabled={busy !== null || code.trim().length === 0} onClick={complete}>
                {busy === "complete" ? "Finalizando…" : "Finalizar conexión"}
              </Button>
            </div>
          </>
        ) : null}

        {result ? (
          <div className="pd-sim__result" style={{ marginTop: "var(--sp-3)" }}>
            <Badge tone={result.ok ? "success" : "danger"} dot>{result.ok ? "OK" : "Error"}</Badge>
            <span className="muted" style={{ fontSize: 13.5 }}>{result.detail}</span>
          </div>
        ) : null}

        {/* Modo avanzado: pegar un refresh token ya obtenido */}
        <details style={{ marginTop: "var(--sp-4)" }}>
          <summary className="muted" style={{ cursor: "pointer", fontSize: 13 }}>Modo avanzado: ya tengo un refresh token</summary>
          <div style={{ marginTop: "var(--sp-3)" }}>
            <ManualMlForm onSaved={onSaved} />
          </div>
        </details>
      </CardBody>
    </Card>
  );
}

/** Ingreso manual de las 3 credenciales ML (para quien ya tiene un refresh token). */
function ManualMlForm({ onSaved }: { onSaved: () => void }): JSX.Element {
  const client = useIpc();
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CredentialTestResult | null>(null);
  const set = (k: string, v: string): void => setValues((s) => ({ ...s, [k]: v }));
  const save = async (): Promise<void> => {
    setBusy(true); setResult(null);
    const res = await client.saveCredentials({ provider: "mercadolibre", values });
    setBusy(false);
    if (res.ok) { setValues({}); onSaved(); setResult({ ok: true, detail: "Credenciales guardadas." }); }
    else setResult({ ok: false, detail: res.error.message });
  };
  return (
    <>
      <div className="pd-form">
        {ML_FIELDS.map((f) => (
          <div className="pd-field pd-form__full" key={f.key}>
            <span className="pd-field__label">{f.label}</span>
            <input className="pd-input" type={f.secret ? "password" : "text"} autoComplete="off" placeholder={f.placeholder} value={values[f.key] ?? ""} aria-label={f.label} onChange={(e) => set(f.key, e.target.value)} />
          </div>
        ))}
      </div>
      <div className="pd-actions">
        <Button size="sm" icon="check" disabled={busy} onClick={save}>{busy ? "Guardando…" : "Guardar credenciales"}</Button>
      </div>
      {result ? (
        <div className="pd-sim__result" style={{ marginTop: "var(--sp-3)" }}>
          <Badge tone={result.ok ? "success" : "danger"} dot>{result.ok ? "OK" : "Error"}</Badge>
          <span className="muted" style={{ fontSize: 13.5 }}>{result.detail}</span>
        </div>
      ) : null}
    </>
  );
}
const AI_FIELDS: readonly FieldDef[] = [
  { key: "apiKey", label: "API Key", secret: true, placeholder: "sk-… / clave del proveedor" },
];
const IMAGE_FIELDS: readonly FieldDef[] = [
  { key: "apiKey", label: "API Key de OpenAI (imágenes)", secret: true, placeholder: "sk-… (platform.openai.com)" },
];

export const AI_PROVIDER_FIELDS = AI_FIELDS;

export function ProviderForm({ provider, title, subtitle, icon, fields, configured, masked, extraProvider, onSaved }: {
  provider: CredentialProvider; title: string; subtitle: string; icon: "marketplace" | "ai";
  fields: readonly FieldDef[]; configured: boolean; masked: string; extraProvider?: string; onSaved: () => void;
}): JSX.Element {
  const client = useIpc();
  const [values, setValues] = useState<Record<string, string>>({});
  const [aiProvider, setAiProvider] = useState(extraProvider ?? "openai");
  const [busy, setBusy] = useState<"save" | "test" | null>(null);
  const [result, setResult] = useState<CredentialTestResult | null>(null);
  const set = (k: string, v: string): void => setValues((s) => ({ ...s, [k]: v }));

  const save = async (): Promise<void> => {
    setBusy("save"); setResult(null);
    const payloadValues = provider === "ai" ? { ...values, provider: aiProvider } : values;
    const res = await client.saveCredentials({ provider, values: payloadValues });
    setBusy(null);
    if (res.ok) { setValues({}); onSaved(); }
    else setResult({ ok: false, detail: res.error.message });
  };
  const test = async (): Promise<void> => {
    setBusy("test"); setResult(null);
    const res = await client.testCredentials(provider);
    setBusy(null);
    setResult(res.ok ? res.value : { ok: false, detail: res.error.message });
  };

  return (
    <Card>
      <CardHeader
        title={<span className="row" style={{ gap: 10 }}><Icon name={icon} size={18} /> {title}</span>}
        subtitle={subtitle}
        action={configured ? <Badge tone="success" dot>Configurado</Badge> : <Badge tone="warning" dot>Sin configurar</Badge>}
      />
      <CardBody>
        {configured ? (
          <p className="muted" style={{ marginTop: 0, fontSize: 13.5 }}>Guardado actualmente: <b className="strong">{masked || "•••"}</b>{extraProvider ? ` · proveedor: ${extraProvider}` : ""}. Ingresa valores nuevos para reemplazar.</p>
        ) : null}
        <div className="pd-form">
          {provider === "ai" ? (
            <div className="pd-field pd-form__full">
              <span className="pd-field__label">Proveedor de IA</span>
              <select className="pd-input" value={aiProvider} onChange={(e) => setAiProvider(e.target.value)} aria-label="Proveedor de IA">
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
              </select>
            </div>
          ) : null}
          {fields.map((f) => (
            <div className="pd-field pd-form__full" key={f.key}>
              <span className="pd-field__label">{f.label}</span>
              <input
                className="pd-input"
                type={f.secret ? "password" : "text"}
                autoComplete="off"
                placeholder={f.placeholder}
                value={values[f.key] ?? ""}
                aria-label={f.label}
                onChange={(e) => set(f.key, e.target.value)}
              />
            </div>
          ))}
        </div>
        <div className="pd-actions">
          <Button variant="secondary" size="sm" icon="pulse" disabled={busy !== null || !configured} onClick={test}>
            {busy === "test" ? "Probando…" : "Probar conexión"}
          </Button>
          <Button size="sm" icon="check" disabled={busy !== null} onClick={save}>
            {busy === "save" ? "Guardando…" : "Guardar"}
          </Button>
        </div>
        {result ? (
          <div className="pd-sim__result" style={{ marginTop: "var(--sp-3)" }}>
            <Badge tone={result.ok ? "success" : "danger"} dot>{result.ok ? "OK" : "Error"}</Badge>
            <span className="muted" style={{ fontSize: 13.5 }}>{result.detail}</span>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/** Configuración del negocio editable (item 3): identidad + parámetros fiscales. */
export function BusinessConfigPanel(): JSX.Element {
  const client = useIpc();
  const { state, reload } = useIpcQuery<BusinessConfig>((c) => c.getBusinessConfig(), []);
  const [draft, setDraft] = useState<BusinessConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const cfg = draft ?? (state.status === "ready" ? state.data : null);
  const set = <K extends keyof BusinessConfig>(k: K, v: BusinessConfig[K]): void => {
    if (cfg) setDraft({ ...cfg, [k]: v });
  };
  const save = async (): Promise<void> => {
    if (!cfg) return;
    setBusy(true); setMsg(null);
    const res = await client.saveBusinessConfig(cfg);
    setBusy(false);
    if (res.ok) { setDraft(null); setMsg({ ok: true, text: "Datos del negocio guardados." }); reload(); }
    else setMsg({ ok: false, text: res.error.message });
  };

  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader
        title={<span className="row" style={{ gap: 10 }}><Icon name="finance" size={18} /> Datos del negocio</span>}
        subtitle="Tu identidad y parámetros fiscales. La app y la IA los usan como fuente única."
        action={<Button size="sm" icon="check" onClick={save} disabled={busy || !cfg}>{busy ? "Guardando…" : "Guardar"}</Button>}
      />
      <CardBody>
        {state.status === "loading" ? <LoadingState label="Cargando…" /> :
         state.status === "error" ? <ErrorState message={state.error.message} onRetry={reload} /> :
         cfg ? (
          <div className="pd-form" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className="pd-field">
              <span className="pd-field__label">Nombre del negocio</span>
              <input className="pd-input" type="text" value={cfg.businessName} aria-label="Nombre del negocio" placeholder="Ej: Comercial Jiménez" onChange={(e) => set("businessName", e.target.value)} />
            </div>
            <div className="pd-field">
              <span className="pd-field__label">RUT</span>
              <input className="pd-input" type="text" value={cfg.rut} aria-label="RUT" placeholder="Ej: 12.345.678-9" onChange={(e) => set("rut", e.target.value)} />
            </div>
            <div className="pd-field">
              <span className="pd-field__label">IVA (%)</span>
              <input className="pd-input" type="number" min={0} max={100} step={0.5} value={cfg.ivaPercent} aria-label="IVA (%)" onChange={(e) => set("ivaPercent", Number(e.target.value))} />
            </div>
            <div className="pd-field">
              <span className="pd-field__label">Moneda (ISO)</span>
              <input className="pd-input" type="text" maxLength={3} value={cfg.currency} aria-label="Moneda (ISO)" placeholder="CLP" onChange={(e) => set("currency", e.target.value.toUpperCase())} />
            </div>
            <div className="pd-field pd-form__full">
              <span className="pd-field__label">ID de usuario de Mercado Libre (opcional)</span>
              <input className="pd-input" type="text" value={cfg.mlUserId} aria-label="ID de usuario de Mercado Libre" placeholder="Se completa al conectar tu cuenta" onChange={(e) => set("mlUserId", e.target.value)} />
            </div>
          </div>
        ) : null}
        {msg ? (
          <div className="pd-sim__result" style={{ marginTop: "var(--sp-3)" }}>
            <Badge tone={msg.ok ? "success" : "danger"} dot>{msg.ok ? "OK" : "Error"}</Badge>
            <span className="muted" style={{ fontSize: 13.5 }}>{msg.text}</span>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

const fmtKb = (bytes: number): string => `${Math.max(1, Math.round(bytes / 1024))} KB`;
const fmtDate = (ms: number): string => new Date(ms).toLocaleString("es-CL");

function BackupPanel(): JSX.Element {
  const client = useIpc();
  const { state, reload } = useIpcQuery<BackupList>((c) => c.listBackups(), []);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const { ask, node } = useConfirm();

  const create = async (): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.createBackup();
    setBusy(false);
    setMsg(res.ok ? `Copia creada: ${res.value.name}` : res.error.message);
    reload();
  };
  const restore = async (name: string): Promise<void> => {
    const ok = await ask({
      title: "Restaurar copia de seguridad",
      message: `Esto SOBRESCRIBE todos tus datos actuales (productos, ventas, inventario, etc.) con el contenido de "${name}". Esta acción no se puede deshacer. ¿Continuar?`,
      confirmLabel: "Sí, restaurar", danger: true,
    });
    if (!ok) return;
    setBusy(true); setMsg(null);
    const res = await client.restoreBackup(name);
    setBusy(false);
    setMsg(res.ok ? "Copia restaurada. Vuelve a abrir cada sección para ver los datos." : res.error.message);
  };
  const exportToFile = async (): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.exportBackup();
    setBusy(false);
    // "cancelled" = el usuario cerró el diálogo: no es un error que mostrar.
    if (!res.ok) { if (res.error.code !== "cancelled") setMsg(res.error.message); return; }
    setMsg(`Copia exportada a: ${res.value.path}`);
  };
  const importFromFile = async (): Promise<void> => {
    const ok = await ask({
      title: "Importar copia desde archivo",
      message: "Vas a elegir un archivo .db externo. Al importarlo se SOBRESCRIBEN todos tus datos actuales con su contenido. Esta acción no se puede deshacer. ¿Continuar?",
      confirmLabel: "Sí, elegir archivo", danger: true,
    });
    if (!ok) return;
    setBusy(true); setMsg(null);
    const res = await client.importBackup();
    setBusy(false);
    if (!res.ok) { if (res.error.code !== "cancelled") setMsg(res.error.message); return; }
    setMsg("Copia importada y restaurada. Vuelve a abrir cada sección para ver los datos.");
    reload();
  };

  const items = state.status === "ready" ? state.data.items : [];
  return (
    <>
    {node}
    <Card style={{ marginTop: "var(--sp-5)" }}>
      <CardHeader
        title={<span className="row" style={{ gap: 10 }}><Icon name="shield" size={18} /> Datos y respaldo</span>}
        subtitle="Crea copias de seguridad de tu base local, expórtalas a un archivo y restáuralas cuando lo necesites"
        action={
          <div className="row" style={{ gap: 8 }}>
            <Button variant="secondary" size="sm" icon="pulse" onClick={importFromFile} disabled={busy}>Importar…</Button>
            <Button variant="secondary" size="sm" icon="pulse" onClick={exportToFile} disabled={busy}>Exportar…</Button>
            <Button size="sm" icon="check" onClick={create} disabled={busy}>{busy ? "Trabajando…" : "Crear copia"}</Button>
          </div>
        }
      />
      <CardBody>
        {msg ? <div className="pd-sim__result" style={{ marginBottom: "var(--sp-3)" }}><Badge tone="brand" dot>Info</Badge><span className="muted" style={{ fontSize: 13.5 }}>{msg}</span></div> : null}
        {state.status === "loading" ? (
          <LoadingState label="Cargando copias…" />
        ) : state.status === "error" ? (
          <ErrorState message={state.error.message} onRetry={reload} />
        ) : items.length === 0 ? (
          <p className="muted" style={{ margin: 0, fontSize: 13.5 }}>Aún no hay copias. Crea la primera con el botón de arriba.</p>
        ) : (
          <div className="tablewrap" style={{ overflowX: "auto" }}>
            <table className="table">
              <thead><tr><th>Copia</th><th>Tamaño</th><th>Fecha</th><th></th></tr></thead>
              <tbody>
                {items.map((b) => (
                  <tr key={b.name}>
                    <td className="strong">{b.name}</td>
                    <td className="tnum">{fmtKb(b.size)}</td>
                    <td className="muted tnum">{fmtDate(b.at)}</td>
                    <td><Button variant="secondary" size="sm" icon="pulse" onClick={() => restore(b.name)} disabled={busy}>Restaurar</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
    </>
  );
}

export function CredentialsScreen(): JSX.Element {
  const { state, reload } = useIpcQuery<CredentialsStatus>((c) => c.getCredentialsStatus(), []);

  if (state.status === "loading") return <LoadingState />;
  if (state.status === "error") return <ErrorState message={state.error.message} onRetry={reload} />;

  const s = state.data;
  return (
    <div>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}>
              <Icon name="shield" size={22} />
            </div>
            <div>
              <h1 className="pagehead__title">Seguridad · Credenciales</h1>
              <p className="pagehead__sub">Conecta tu cuenta de Mercado Libre y tu proveedor de IA. Las llaves se guardan cifradas por el sistema operativo y nunca se muestran completas.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="callout warn" style={{ marginBottom: "var(--sp-5)" }}>
        <div className="bar" />
        <div className="body"><b>Tus llaves son privadas.</b> Se cifran en tu equipo (DPAPI) y solo se usan para conectar con las APIs. No las compartas con nadie.</div>
      </div>

      <BusinessConfigPanel />

      <SyncSettingsPanel />

      <div className="pd-cols" style={{ gridTemplateColumns: "1fr 1fr", alignItems: "start" }}>
        <MlConnectPanel configured={s.mercadolibre.configured} masked={s.mercadolibre.masked} onSaved={reload} />
        <ProviderForm
          provider="ai" title="Inteligencia Artificial" subtitle="API para las funciones de IA (OpenAI / Anthropic)" icon="ai"
          fields={AI_FIELDS} configured={s.ai.configured} masked={s.ai.masked} {...(s.ai.extra ? { extraProvider: s.ai.extra } : {})} onSaved={reload}
        />
      </div>

      <div style={{ marginTop: "var(--sp-5)" }}>
        <ProviderForm
          provider="images" title="API de imágenes (OpenAI)" subtitle="SÓLO para generar imágenes de tus publicaciones. Independiente de tu IA general (Anthropic)." icon="ai"
          fields={IMAGE_FIELDS} configured={s.images.configured} masked={s.images.masked} onSaved={reload}
        />
      </div>

      {/* Alertas por correo + estado del servidor: solo en la versión web/nube. */}
      {isWebClient() ? <AlertsPanel /> : null}
      {isWebClient() ? <ServerStatusPanel /> : null}

      {/* Respaldos y auto-actualización son funciones de la app de ESCRITORIO (Electron);
          en la versión web (PWA) no aplican, así que se ocultan. */}
      {!isWebClient() ? (
        <>
          <BackupPanel />
          <UpdatesPanel />
        </>
      ) : null}
    </div>
  );
}
